import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { RotateCcw, Package, Warehouse, Users, ArrowUpRight, ArrowDownLeft, Plus, X, Trash2, AlertTriangle, Zap, Pencil, Settings2 } from "lucide-react";
import { toast } from "sonner";

export default function Retornables() {
  const [data, setData] = useState({ kpis: {}, types: [], customers: [] });
  const [movs, setMovs] = useState([]);
  const [sales, setSales] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [tab, setTab] = useState("estado");
  const [movForm, setMovForm] = useState(null);
  const [autoSaleId, setAutoSaleId] = useState("");
  const [typeForm, setTypeForm] = useState(null);
  const [toDelMov, setToDelMov] = useState(null);

  const load = async () => {
    const [s, m, sl, cu] = await Promise.all([api.get("/returnables/summary"), api.get("/returnables/movements"), api.get("/sales"), api.get("/customers")]);
    setData(s.data);
    setMovs(m.data);
    setSales(sl.data);
    setCustomers(cu.data);
  };
  useEffect(() => { load(); }, []);

  const kpis = data.kpis || {};
  const runAuto = async () => {
    if (!autoSaleId) return toast.error("Selecciona una venta");
    try {
      const { data } = await api.post("/returnables/from-sale", { sale_id: autoSaleId });
      toast.success(`${data.total_kg} kg → ${data.tambos} tambos + ${data.tarimas} tarimas generadas`);
      setAutoSaleId("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="max-w-screen-2xl mx-auto p-6 lg:p-8 space-y-6" data-testid="retornables-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-outfit font-bold text-3xl tracking-tight text-stone-900 dark:text-stone-100">Retornables</h1>
          <p className="text-sm text-stone-500 dark:text-stone-400 mt-1">Control de tambos, tarimas y otros envases que salen con el producto y regresan a planta.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <KPI icon={Warehouse} label="En planta ahora" value={kpis.total_in_plant || 0} sub="Disponibles para salir" tone="emerald" />
        <KPI icon={ArrowUpRight} label="Con clientes" value={kpis.total_with_customers || 0} sub="Fuera de planta" tone="blue" />
        <KPI icon={Users} label="Clientes con retornables" value={kpis.customers_with_returnables || 0} sub="Total con adeudo" tone="stone" />
        <KPI icon={AlertTriangle} label="Con retraso" value={kpis.customers_with_alerts || 0} sub="Superan umbral configurado" tone="red" />
      </div>

      <div className="flex gap-1 border-b border-stone-200 dark:border-stone-800">
        {[
          { k: "estado", i: Warehouse, l: "Estado por tipo" },
          { k: "clientes", i: Users, l: "Con clientes" },
          { k: "movimientos", i: RotateCcw, l: "Movimientos" },
          { k: "catalogo", i: Settings2, l: "Catálogo" },
        ].map((t) => <TabBtn key={t.k} active={tab === t.k} onClick={() => setTab(t.k)} icon={t.i} label={t.l} testId={`ret-tab-${t.k}`} />)}
      </div>

      {tab === "estado" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.types.map((t) => (
            <Card key={t.id} className="p-5 border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-9 h-9 rounded-md bg-[#1E3A8A]/10 text-[#1E3A8A] dark:text-blue-400 flex items-center justify-center"><Package className="w-4 h-4" strokeWidth={1.75} /></div>
                <div>
                  <div className="font-outfit font-semibold text-stone-900 dark:text-stone-100">{t.name}</div>
                  <div className="text-xs text-stone-500 dark:text-stone-400">Capacidad {t.capacity_kg} kg · Alerta a {t.alert_days_out} días</div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-3">
                <MiniStat label="Stock inicial" value={t.stock_initial} />
                <MiniStat label="En planta" value={t.in_plant} tone="emerald" />
                <MiniStat label="Con clientes" value={t.with_customers} tone="blue" />
              </div>
              <div className="text-xs text-stone-500 dark:text-stone-400 mt-3 pt-3 border-t border-stone-100 dark:border-stone-800">
                Total flota: <b className="text-stone-800 dark:text-stone-200">{t.total_owned}</b> · Total salidas históricas: {t.total_out} · Regresos: {t.total_in}
              </div>
            </Card>
          ))}
        </div>
      )}

      {tab === "clientes" && (
        <Card className="border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-100 dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800">
                <tr>{["Cliente", "Retornables pendientes", "Días fuera (más antiguo)", "Alertas"].map((h) => <th key={h} className="px-3 py-2.5 text-left text-[10px] font-outfit font-semibold uppercase tracking-wider text-stone-600 dark:text-stone-400">{h}</th>)}</tr>
              </thead>
              <tbody>
                {data.customers.length === 0 ? (
                  <tr><td colSpan={4} className="px-4 py-12 text-center text-sm text-stone-500">Sin retornables fuera de planta.</td></tr>
                ) : data.customers.map((c) => {
                  const oldestDays = c.by_type.reduce((max, bt) => Math.max(max, bt.days_out || 0), 0);
                  return (
                    <tr key={c.customer_id || c.customer_name} className="border-b border-stone-100 dark:border-stone-800 hover:bg-stone-50 dark:hover:bg-stone-900/50" data-testid={`ret-cust-${c.customer_name}`}>
                      <td className="px-3 py-3 text-sm font-medium text-stone-900 dark:text-stone-100">{c.customer_name}</td>
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap gap-1">
                          {c.by_type.filter((bt) => bt.outstanding > 0).map((bt) => (
                            <span key={bt.type_code} className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded font-medium ${bt.overdue ? "bg-red-100 dark:bg-red-950/40 text-red-800 dark:text-red-300" : "bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300"}`}>
                              <Package className="w-3 h-3" /> {bt.outstanding} × {bt.type_name}
                              {bt.days_out != null && <span className="text-[10px] opacity-70">· {bt.days_out}d</span>}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-sm tabular-nums">
                        <span className={oldestDays > 30 ? "text-red-600 dark:text-red-400 font-semibold" : "text-stone-700 dark:text-stone-300"}>{oldestDays} días</span>
                      </td>
                      <td className="px-3 py-3">
                        {c.alerts > 0
                          ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-red-100 dark:bg-red-950/50 text-red-800 dark:text-red-300 text-[10px] uppercase tracking-wider font-outfit font-semibold"><AlertTriangle className="w-3 h-3" /> {c.alerts} tipo(s) vencido(s)</span>
                          : <span className="text-xs text-emerald-700 dark:text-emerald-400">Al día</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {tab === "movimientos" && (
        <>
          <Card className="p-4 border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm space-y-3">
            <div className="flex flex-wrap gap-2 items-end">
              <Button onClick={() => setMovForm({ type_code: data.types[0]?.code || "", direction: "out", qty: 1, movement_date: new Date().toISOString().slice(0, 10), customer_id: "", customer_name: "", related_sale_folio: "", notes: "" })} data-testid="new-mov-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]">
                <Plus className="w-4 h-4 mr-1.5" /> Movimiento manual
              </Button>
              <div className="flex-1 min-w-[300px] flex gap-2 items-end p-3 rounded-md bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900">
                <Zap className="w-4 h-4 text-[#1E3A8A] dark:text-blue-400 mt-2 flex-shrink-0" />
                <div className="flex-1">
                  <Label className="text-[10px] uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Auto-generar desde una venta</Label>
                  <Select value={autoSaleId || "_none"} onValueChange={(v) => setAutoSaleId(v === "_none" ? "" : v)}>
                    <SelectTrigger className="mt-1" data-testid="auto-sale-select"><SelectValue placeholder="Elegir venta con crema/cuajada…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">— Elegir —</SelectItem>
                      {sales.filter((s) => s.items?.some((it) => it.source_type === "cream" || it.source_type === "curd")).map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.folio} · {s.customer_name} · {s.sale_date}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={runAuto} disabled={!autoSaleId} data-testid="auto-gen-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">Generar</Button>
              </div>
            </div>
          </Card>

          {movForm && <MovementForm form={movForm} setForm={setMovForm} types={data.types} customers={customers} onCancel={() => setMovForm(null)} onSaved={() => { setMovForm(null); load(); }} />}

          <Card className="border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm overflow-hidden">
            <div className="px-5 py-3.5 border-b border-stone-200 dark:border-stone-800 flex items-center gap-2">
              <RotateCcw className="w-4 h-4 text-stone-500" />
              <h3 className="font-outfit font-semibold text-stone-900 dark:text-stone-100">Historial</h3>
              <span className="text-xs text-stone-500 ml-auto">{movs.length} movimientos</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-stone-100 dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800">
                  <tr>{["Fecha", "Dirección", "Tipo", "Cantidad", "Cliente", "Venta", "Origen", "Notas", ""].map((h) => <th key={h} className="px-3 py-2.5 text-left text-[10px] font-outfit font-semibold uppercase tracking-wider text-stone-600 dark:text-stone-400">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {movs.length === 0 ? (
                    <tr><td colSpan={9} className="px-4 py-8 text-center text-sm text-stone-500">Sin movimientos.</td></tr>
                  ) : movs.map((m) => (
                    <tr key={m.id} className="border-b border-stone-100 dark:border-stone-800 hover:bg-stone-50 dark:hover:bg-stone-900/50">
                      <td className="px-3 py-2 text-xs tabular-nums font-mono text-stone-600 dark:text-stone-400">{m.movement_date}</td>
                      <td className="px-3 py-2">
                        {m.direction === "out"
                          ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-950/40 text-blue-800 dark:text-blue-300 text-[10px] uppercase tracking-wider font-outfit font-semibold"><ArrowUpRight className="w-3 h-3" /> Salida</span>
                          : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-100 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 text-[10px] uppercase tracking-wider font-outfit font-semibold"><ArrowDownLeft className="w-3 h-3" /> Regreso</span>}
                      </td>
                      <td className="px-3 py-2 text-sm text-stone-800 dark:text-stone-200">{m.type_name}</td>
                      <td className="px-3 py-2 text-sm tabular-nums font-semibold text-stone-900 dark:text-stone-100">{m.qty}</td>
                      <td className="px-3 py-2 text-sm text-stone-700 dark:text-stone-300">{m.customer_name || "—"}</td>
                      <td className="px-3 py-2 text-xs font-mono text-[#1E3A8A] dark:text-blue-400">{m.related_sale_folio || "—"}</td>
                      <td className="px-3 py-2 text-[10px] uppercase tracking-wider text-stone-500">{m.source === "auto_sale" ? "Auto (venta)" : "Manual"}</td>
                      <td className="px-3 py-2 text-xs text-stone-500 dark:text-stone-400 max-w-[220px] truncate">{m.notes || "—"}</td>
                      <td className="px-3 py-2"><button onClick={() => setToDelMov(m)} className="p-1.5 rounded-md text-stone-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40"><Trash2 className="w-3.5 h-3.5" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {tab === "catalogo" && (
        <>
          <div className="flex justify-end">
            <Button onClick={() => setTypeForm({ code: "", name: "", capacity_kg: 0, stock_initial: 0, alert_days_out: 30, active: true, notes: "" })} className="bg-[#1E3A8A] hover:bg-[#1E40AF]"><Plus className="w-4 h-4 mr-1.5" /> Nuevo tipo</Button>
          </div>
          {typeForm && <TypeForm form={typeForm} setForm={setTypeForm} onCancel={() => setTypeForm(null)} onSaved={() => { setTypeForm(null); load(); }} />}
          <Card className="border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-stone-100 dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800">
                  <tr>{["Código", "Nombre", "Capacidad kg", "Stock inicial", "Alerta días", "Activo", ""].map((h) => <th key={h} className="px-3 py-2.5 text-left text-[10px] font-outfit font-semibold uppercase tracking-wider text-stone-600 dark:text-stone-400">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {data.types.map((t) => (
                    <tr key={t.id} className="border-b border-stone-100 dark:border-stone-800">
                      <td className="px-3 py-2 text-sm font-mono text-[#1E3A8A] dark:text-blue-400">{t.code}</td>
                      <td className="px-3 py-2 text-sm font-medium text-stone-900 dark:text-stone-100">{t.name}</td>
                      <td className="px-3 py-2 text-sm tabular-nums">{t.capacity_kg}</td>
                      <td className="px-3 py-2 text-sm tabular-nums">{t.stock_initial}</td>
                      <td className="px-3 py-2 text-sm tabular-nums">{t.alert_days_out}</td>
                      <td className="px-3 py-2">{t.active ? <span className="text-xs text-emerald-700 dark:text-emerald-400">Activo</span> : <span className="text-xs text-stone-400">Inactivo</span>}</td>
                      <td className="px-3 py-2"><button onClick={() => setTypeForm({ ...t })} className="p-1.5 rounded-md text-stone-500 hover:text-[#1E3A8A] hover:bg-blue-50 dark:hover:bg-blue-950/40"><Pencil className="w-4 h-4" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      <AlertDialog open={!!toDelMov} onOpenChange={(o) => !o && setToDelMov(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>¿Eliminar movimiento?</AlertDialogTitle><AlertDialogDescription>{toDelMov?.qty} × {toDelMov?.type_name} ({toDelMov?.direction === "out" ? "salida" : "regreso"}) de {toDelMov?.customer_name || "—"}</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={async () => { try { await api.delete(`/returnables/movements/${toDelMov.id}`); toast.success("Movimiento eliminado"); setToDelMov(null); load(); } catch { toast.error("Error"); } }} className="bg-red-600 hover:bg-red-700 text-white">Eliminar</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const TabBtn = ({ active, onClick, icon: Icon, label, testId }) => (
  <button onClick={onClick} data-testid={testId} className={`px-4 py-2.5 text-sm font-outfit font-medium flex items-center gap-2 border-b-2 -mb-px transition-colors ${active ? "border-[#1E3A8A] text-[#1E3A8A] dark:text-blue-400 dark:border-blue-400" : "border-transparent text-stone-500 hover:text-stone-800 dark:hover:text-stone-200"}`}>
    <Icon className="w-4 h-4" strokeWidth={1.75} />
    {label}
  </button>
);

const MovementForm = ({ form, setForm, types, customers, onCancel, onSaved }) => {
  const pickCust = (id) => {
    const c = customers.find((x) => x.id === id);
    if (!c) return;
    setForm({ ...form, customer_id: c.id, customer_name: c.name });
  };
  const submit = async (e) => {
    e.preventDefault();
    if (form.qty <= 0) return toast.error("Cantidad inválida");
    try {
      await api.post("/returnables/movements", { ...form, qty: parseInt(form.qty) });
      toast.success("Movimiento registrado");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  return (
    <Card className="p-5 border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-outfit font-semibold text-lg text-stone-900 dark:text-stone-100">Nuevo movimiento</h2>
        <button onClick={onCancel} className="text-stone-400 hover:text-stone-700"><X className="w-5 h-5" /></button>
      </div>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Tipo *</Label>
          <Select value={form.type_code} onValueChange={(v) => setForm({ ...form, type_code: v })}>
            <SelectTrigger className="mt-1" data-testid="mov-type"><SelectValue /></SelectTrigger>
            <SelectContent>{types.map((t) => <SelectItem key={t.code} value={t.code}>{t.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Dirección *</Label>
          <Select value={form.direction} onValueChange={(v) => setForm({ ...form, direction: v })}>
            <SelectTrigger className="mt-1" data-testid="mov-direction"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="out">Salida (entrega a cliente)</SelectItem>
              <SelectItem value="in">Regreso (vuelve a planta)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Cantidad *</Label>
          <Input type="number" min="1" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} className="mt-1" data-testid="mov-qty" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Fecha</Label>
          <Input type="date" value={form.movement_date} onChange={(e) => setForm({ ...form, movement_date: e.target.value })} className="mt-1" />
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Cliente</Label>
          <Select value={form.customer_id || "_none"} onValueChange={(v) => v === "_none" ? setForm({ ...form, customer_id: "", customer_name: "" }) : pickCust(v)}>
            <SelectTrigger className="mt-1"><SelectValue placeholder="Elegir cliente…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">— Sin cliente —</SelectItem>
              {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value, customer_id: "" })} placeholder="… o captura nombre libre" className="mt-1" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Folio venta relacionada</Label>
          <Input value={form.related_sale_folio} onChange={(e) => setForm({ ...form, related_sale_folio: e.target.value })} placeholder="VTA-..." className="mt-1" />
        </div>
        <div className="md:col-span-4">
          <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Notas</Label>
          <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1" />
        </div>
        <div className="md:col-span-4 flex gap-2 justify-end pt-2 border-t border-stone-200 dark:border-stone-800">
          <Button type="button" variant="outline" onClick={onCancel}>Cancelar</Button>
          <Button type="submit" data-testid="mov-submit-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]">Registrar</Button>
        </div>
      </form>
    </Card>
  );
};

const TypeForm = ({ form, setForm, onCancel, onSaved }) => {
  const submit = async (e) => {
    e.preventDefault();
    if (!form.code.trim() || !form.name.trim()) return toast.error("Código y nombre requeridos");
    try {
      const payload = { code: form.code, name: form.name, capacity_kg: parseFloat(form.capacity_kg), stock_initial: parseInt(form.stock_initial) || 0, alert_days_out: parseInt(form.alert_days_out) || 30, active: !!form.active, notes: form.notes || "" };
      if (form.id) await api.put(`/returnables/types/${form.id}`, payload);
      else await api.post("/returnables/types", payload);
      toast.success(form.id ? "Actualizado" : "Creado");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  return (
    <Card className="p-5 border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-outfit font-semibold text-lg">{form.id ? "Editar tipo" : "Nuevo tipo"}</h2>
        <button onClick={onCancel}><X className="w-5 h-5" /></button>
      </div>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div><Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600">Código *</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="mt-1" disabled={!!form.id} /></div>
        <div className="md:col-span-2"><Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600">Nombre *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
        <div><Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600">Capacidad kg</Label><Input type="number" step="0.1" value={form.capacity_kg} onChange={(e) => setForm({ ...form, capacity_kg: e.target.value })} className="mt-1" /></div>
        <div><Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600">Stock inicial (en planta)</Label><Input type="number" min="0" value={form.stock_initial} onChange={(e) => setForm({ ...form, stock_initial: e.target.value })} className="mt-1" /></div>
        <div><Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600">Alerta después de X días</Label><Input type="number" min="1" value={form.alert_days_out} onChange={(e) => setForm({ ...form, alert_days_out: e.target.value })} className="mt-1" /></div>
        <div className="md:col-span-3"><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Activo</label></div>
        <div className="md:col-span-3 flex gap-2 justify-end pt-2 border-t">
          <Button type="button" variant="outline" onClick={onCancel}>Cancelar</Button>
          <Button type="submit" className="bg-[#1E3A8A] hover:bg-[#1E40AF]">Guardar</Button>
        </div>
      </form>
    </Card>
  );
};

const MiniStat = ({ label, value, tone = "stone" }) => (
  <div className="text-center py-2 rounded-md bg-stone-50 dark:bg-stone-900">
    <div className={`text-2xl font-outfit font-bold tabular-nums ${tone === "emerald" ? "text-emerald-700 dark:text-emerald-400" : tone === "blue" ? "text-[#1E3A8A] dark:text-blue-400" : "text-stone-900 dark:text-stone-100"}`}>{value}</div>
    <div className="text-[10px] uppercase tracking-wider font-outfit text-stone-500">{label}</div>
  </div>
);

const TONES = {
  blue: "bg-[#1E3A8A]/10 text-[#1E3A8A] dark:text-blue-400",
  emerald: "bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400",
  red: "bg-red-100 dark:bg-red-950/40 text-red-700 dark:text-red-400",
  stone: "bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300",
};

const KPI = ({ icon: Icon, label, value, sub, tone = "blue" }) => (
  <Card className="p-5 border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm">
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs font-outfit font-semibold uppercase tracking-[0.1em] text-stone-500 dark:text-stone-400">{label}</div>
        <div className="mt-2 font-outfit font-semibold text-2xl text-stone-900 dark:text-stone-100 tabular-nums">{value}</div>
        {sub && <div className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">{sub}</div>}
      </div>
      <div className={`w-9 h-9 rounded-md flex items-center justify-center ${TONES[tone]}`}>
        <Icon className="w-4 h-4" strokeWidth={1.75} />
      </div>
    </div>
  </Card>
);
